import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStation } from "@/context/StationContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { APP_VERSION } from "@/lib/version";
import { pendingCount, flushPending } from "@/lib/offline";
import {
  LayoutDashboard, Fuel, ClipboardList, LineChart, Users, LogOut, Menu, X, KeyRound, History, Droplets, RefreshCw, WifiOff, BadgePercent, ClipboardCheck, FileText, Table2, HandCoins, Scale,
} from "lucide-react";
import { useEffect, useState } from "react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/operations", label: "Daily Operations", icon: ClipboardList, testid: "nav-operations" },
  { to: "/operations-sheet", label: "Operations Sheet", icon: Table2, testid: "nav-ops-sheet" },
  { to: "/shift-reconciliation", label: "Shift Reconciliation", icon: Scale, testid: "nav-shift" },
  { to: "/summary", label: "Summary / Corrections", icon: ClipboardCheck, managerOnly: true, testid: "nav-summary" },
  { to: "/pnl", label: "P&L Report", icon: LineChart, testid: "nav-pnl" },
  { to: "/budi", label: "BUDI Claims", icon: BadgePercent, testid: "nav-budi" },
  { to: "/credit", label: "Credit Customers", icon: HandCoins, testid: "nav-credit" },
  { to: "/kpdn", label: "KPDN Report", icon: FileText, managerOnly: true, testid: "nav-kpdn" },
  { to: "/price-history", label: "Price History", icon: History, testid: "nav-price-history" },
  { to: "/stock", label: "Stock & Alerts", icon: Droplets, testid: "nav-stock" },
  { to: "/stations", label: "Stations & Pumps", icon: Fuel, managerOnly: true, testid: "nav-stations" },
  { to: "/staff", label: "Staff & Invites", icon: Users, managerOnly: true, testid: "nav-staff" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = nav.filter((n) => !n.managerOnly || user?.role === "manager");

  return (
    <div className="min-h-screen bg-[#0B0F17] text-[#F9FAFB] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-full w-64 bg-[#111827] border-r border-[#1F2937] flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-[#1F2937] flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Fuel className="w-5 h-5 text-black" />
          </div>
          <div>
            <div className="font-[Outfit] font-bold text-lg leading-none tracking-tight">Fuel Tracer</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mt-1">Station ERP</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 font-medium"
                    : "text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1F2937]">
          <div className="mb-3">
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-[#9CA3AF] font-mono uppercase tracking-wider">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <NavLink
            to="/change-password"
            onClick={() => setOpen(false)}
            data-testid="nav-change-password"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#9CA3AF] hover:text-white hover:bg-[#1F2937] transition-colors mb-2"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </NavLink>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-button"
            className="w-full border-[#374151] bg-transparent hover:bg-[#1F2937] text-[#9CA3AF]"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <TopBar onMenu={() => setOpen(!open)} menuOpen={open} />
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto safe-x">{children}</main>
      </div>
    </div>
  );
}

function TopBar({ onMenu, menuOpen }) {
  const { stations, stationId, setStationId } = useStation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(pendingCount());

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/version");
        if (active && data?.version && data.version !== APP_VERSION) setUpdateAvailable(true);
      } catch {}
    };
    check();
    const id = setInterval(check, 60000);
    window.addEventListener("focus", check);
    const sw = navigator.serviceWorker;
    const hadController = !!(sw && sw.controller);
    const onCtrl = () => { if (hadController) setUpdateAvailable(true); };
    if (sw) sw.addEventListener("controllerchange", onCtrl);
    return () => {
      active = false; clearInterval(id); window.removeEventListener("focus", check);
      if (sw) sw.removeEventListener("controllerchange", onCtrl);
    };
  }, []);

  useEffect(() => {
    const sync = async () => {
      setOnline(true);
      const n = await flushPending();
      setPending(pendingCount());
      if (n > 0) {
        toast.success(`Synced ${n} offline ${n === 1 ? "entry" : "entries"}`);
        window.dispatchEvent(new Event("ft-synced"));
      }
    };
    const off = () => setOnline(false);
    const q = () => setPending(pendingCount());
    window.addEventListener("online", sync);
    window.addEventListener("offline", off);
    window.addEventListener("ft-queue-changed", q);
    if (navigator.onLine) sync();
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", off);
      window.removeEventListener("ft-queue-changed", q);
    };
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {}
    toast.success("Refreshing to latest…");
    setTimeout(() => window.location.reload(), 300);
  };

  return (
    <>
      <header className="sticky top-0 z-20 safe-top flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-3 border-b border-[#1F2937] bg-[#0B0F17]/85 backdrop-blur-xl">
        <button onClick={onMenu} data-testid="menu-toggle" className="lg:hidden text-white">
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span className="lg:hidden font-[Outfit] font-bold">Fuel Tracer</span>
        <div className="flex-1" />
        {stations.length > 0 && (
          <Select value={stationId} onValueChange={setStationId}>
            <SelectTrigger data-testid="global-station-filter" className="h-8 w-36 sm:w-48 bg-[#111827] border-[#374151] text-white text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              <SelectItem value="all">All Stations</SelectItem>
              {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {!online && (
          <span data-testid="offline-chip" className="flex items-center gap-1.5 text-xs text-red-400">
            <WifiOff className="w-4 h-4" /> <span className="hidden sm:inline">Offline</span>
          </span>
        )}
        {pending > 0 && (
          <span data-testid="pending-chip" title="Entries saved offline, waiting to sync"
            className="text-xs font-mono px-2 py-1 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-400">
            {pending} pending
          </span>
        )}
        {updateAvailable && (
          <span data-testid="update-available" className="hidden md:flex items-center gap-1.5 text-xs text-amber-400">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> New version
          </span>
        )}
        <span
          data-testid="app-version"
          title="App version — everyone on this number is on the same build"
          className={`font-mono text-xs px-2.5 py-1 rounded-lg border ${updateAvailable ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-[#374151] bg-[#111827] text-[#9CA3AF]"}`}
        >
          v{APP_VERSION}
        </span>
        <Button
          onClick={refresh}
          disabled={refreshing}
          data-testid="refresh-button"
          size="sm"
          className={`font-semibold ${updateAvailable ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}
        >
          <RefreshCw className={`w-4 h-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">{updateAvailable ? "Update" : "Refresh"}</span>
        </Button>
      </header>

      {updateAvailable && (
        <div data-testid="update-banner"
          className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-2.5 bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-sm animate-fadeUp">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
          <span className="flex-1">A new version of Fuel Tracer is available.</span>
          <button onClick={refresh} data-testid="update-tap"
            className="font-semibold text-black bg-amber-400 hover:bg-amber-300 px-3 py-1 rounded-lg shrink-0">
            Tap to update
          </button>
        </div>
      )}
    </>
  );
}
